import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import { useEventLogger } from '../../lib/eventLogger/EventLoggerProvider';
import { usePageTimeTracking } from '../../lib/pageTimeTracking/usePageTimeTracking';
import { markProfileModified } from './AutoApplySetupSessionState';

const PROFILE_MODIFIED_RETURN_SOURCES = new Set([
    'profile-preferences-edit',
    'profile-resume-edit',
]);

const REVIEW_BEFORE_SUBMIT_PATH = '/auto-apply-setup/review/review-before-submit';

/**
 * Root component for all /auto-apply-setup/* routes.
 * Handles page view and stayed event logging for the entire flow,
 * including terms and report-issue pages. In the disabled treatment it
 * canonicalizes Review variants to RBS; automation-level routes remain page-managed.
 */
const AutoApplySetupRoot: React.FC = () => {
    const { pathname, search: locationSearch } = useLocation();
    const eventLogger = useEventLogger();
    const [search] = useSearchParams();
    const { getGroup } = useProctorGroups();
    const isApplyForMeDisabled = getGroup('apply_for_me_sfm_tog')?.value === 0;

    usePageTimeTracking(pathname, (stayedPage, timeStayedOnPageMs) => {
        eventLogger.logAutoApplySetupPageStayedEvent?.({ stayedPage, timeStayedOnPageMs });
    });

    useEffect(() => {
        eventLogger.logAutoApplySetupPageViewEvent?.({ currentPage: pathname });
    }, [pathname, eventLogger]);

    useEffect(() => {
        const fromParam = search.get('from');
        if (fromParam && PROFILE_MODIFIED_RETURN_SOURCES.has(fromParam)) {
            markProfileModified();
        }
    }, [search]);

    // Bare Review can retain legacy activation behavior, so normalize it along with the
    // Apply For Me variant. Automation-level routes safely filter their own options.
    const isBlockedApplyForMePath =
        pathname.startsWith('/auto-apply-setup/review') && pathname !== REVIEW_BEFORE_SUBMIT_PATH;

    if (isApplyForMeDisabled && isBlockedApplyForMePath) {
        return <Navigate to={`${REVIEW_BEFORE_SUBMIT_PATH}${locationSearch}`} replace />;
    }

    return <Outlet />;
};

export default AutoApplySetupRoot;
